import { useContext, useEffect } from 'react';
import { FileContext } from '../context/FileContext';
import { PDFDocument } from 'pdf-lib';

export function useEnvelope() {
  const {
    documentRef,
    envelopeData,
    setEnvelopeData,
    addedFormFields,
    pagesAddedByUser,
    updateFileId,
    updateFormFields,
    setPagesAddedByUser,
    pdfDimensions,
    setPageDeleteIndex,
    isTemplate,
  } = useContext(FileContext);

  const activeFileId = envelopeData?.activeFileId;

  const activeFile = envelopeData?.pdfFileData?.find(
    (x) => x.id == activeFileId,
  )?.updated_file;

  const activeFileType = envelopeData?.pdfFileData?.find(
    (x) => x.id == activeFileId,
  )?.filetype;

  const fileLinkTop = isTemplate?.status
    ? null
    : envelopeData?.fileLinkTop || null;
  const fileLinkBottom = envelopeData?.fileLinkBottom || null;
  const isAdhoc = envelopeData?.isAdhoc || false;

  const updateEnvelopeData = (key, val) => {
    setEnvelopeData((prev) => {
      return {
        ...prev,
        [key]: val,
      };
    });
  };

  const setIsAdhoc = (value) => {
    updateEnvelopeData('isAdhoc', value);
  };

  const setFileTopAndBottom = (files, id) => {
    const indexOfActiveFile = files?.findIndex((x) => x['_id'] == id);
    updateEnvelopeData('fileLinkTop', null);
    updateEnvelopeData('fileLinkBottom', null);

    if (indexOfActiveFile > 0) {
      updateEnvelopeData('fileLinkTop', files[indexOfActiveFile - 1]);
    }
    if (indexOfActiveFile < files?.length - 1) {
      updateEnvelopeData('fileLinkBottom', files[indexOfActiveFile + 1]);
    }
  };

  const updateInitialPlaceholdersInEnvelope = (val) => {
    const placeholders = [];
    val.forEach((x) => {
      const uniqueSigners = x.fields?.reduce((acc, obj) => {
        if (
          obj.signer &&
          !acc.some((signer) => signer.value === obj.signer.value)
        ) {
          acc.push(obj.signer);
        }
        return acc;
      }, []);
      const newPlaceholder = {
        assetId: x._id.toString(),
        fields: x.fields,
        pages_added_by_users: [],
        signers_settings: uniqueSigners,
      };
      placeholders.push(newPlaceholder);
    });
    updateEnvelopeData('placeHolders', placeholders);
  };

  const setAttachedFilesInEnvelope = (val, isAdhoc = false) => {
    updateEnvelopeData('attachedFiles', val);
    isAdhoc && updateInitialPlaceholdersInEnvelope(val);
  };

  const setActiveFileId = (val) => {
    updateEnvelopeData('activeFileId', val);
    updateFileId(val);
  };

  const updateAllFileStates = (data, fileId) => {
    updateFormFields(data.fields);
    updateFileId(fileId);
    setPagesAddedByUser(data.pages_added_by_users);
  };

  const updateEnvelopeFormData = (key) => {
    if (activeFileId) {
      const placeHolders = envelopeData?.placeHolders || [];
      const uniqueSigners = addedFormFields?.reduce((acc, obj) => {
        if (
          obj.signer &&
          !acc.some((signer) => signer.value === obj.signer.value)
        ) {
          acc.push(obj.signer);
        }
        return acc;
      }, []);
      const newPlaceholder = {
        [key]: activeFileId,
        fields: addedFormFields,
        pages_added_by_users: pagesAddedByUser,
        signers_settings: uniqueSigners,
      };
      const index = placeHolders?.findIndex(
        (placeholder) => placeholder?.[key] == activeFileId,
      );
      if (index !== -1) {
        // Replace existing object
        placeHolders[index] = newPlaceholder;
      } else {
        // Add new object
        placeHolders.push(newPlaceholder);
      }
      updateEnvelopeData('placeHolders', placeHolders);
    }
  };

  const updatePdfDataForPageAddRemove = ({ val, file = false }) => {
    const existingFileData = envelopeData?.pdfFileData || [];
    const index = existingFileData?.findIndex((x) => x.id === val._id);
    if (index < 0) {
      // if there is no object with matching id
      const fileData = {
        id: val?._id,
        original_file: val?.file,
        updated_file: val?.file,
        file_name: val?.file_name,
        board_id: val?.board_id,
        account_id: val?.account_id,
        filetype: val.fileType,
      };
      existingFileData.push(fileData);
      updateEnvelopeData('pdfFileData', existingFileData);
    } else {
      if (file) {
        // if there is object with matching id and base64 url has been changed
        existingFileData[index] = {
          ...existingFileData[index],
          updated_file: file,
        };
        updateEnvelopeData('pdfFileData', existingFileData);
      }
    }
  };

  const setActiveFileData = (val, isAdhoc = false, file) => {
    //setData in original file from api
    updatePdfDataForPageAddRemove({ val, file });

    const key = isAdhoc ? 'assetId' : 'fileId';
    const placeHolder = envelopeData?.placeHolders?.find(
      (obj) => obj[key] == val?._id,
    );

    updateEnvelopeData('activeFile', val);

    if (!isTemplate?.status) {
      updateEnvelopeFormData(key);

      if (placeHolder) {
        updateAllFileStates(placeHolder, placeHolder[key]);
      } else {
        if (val) updateAllFileStates(val, val['_id']);
      }
    }
  };

  const addPageInEnvelopePDF = async (pageIndex) => {
    try {
      const pageSize = [
        pdfDimensions?.width / pdfDimensions?.scale,
        pdfDimensions?.height / pdfDimensions?.scale,
      ];
      const pdfDoc = await PDFDocument.load(envelopeData?.activeFile?.file);

      pdfDoc.insertPage(pageIndex, pageSize);
      const base64DataUri = await pdfDoc.saveAsBase64({ dataUri: true });
      updatePdfDataForPageAddRemove({
        val: envelopeData.activeFile,
        file: base64DataUri,
      });
      updateEnvelopeData('activeFile', {
        ...envelopeData.activeFile,
        file: base64DataUri,
      });
      setPagesAddedByUser((prevPageIndexes) => {
        const updatedIndexes = [];
        prevPageIndexes?.forEach((x) => {
          if (x >= pageIndex) {
            updatedIndexes.push(x + 1);
          } else {
            updatedIndexes.push(x);
          }
        });
        updatedIndexes.push(pageIndex);
        return updatedIndexes;
      });
      const updatedFields = addedFormFields?.map((field) => {
        if (field.formField.pageIndex >= pageIndex) {
          return {
            ...field,
            formField: {
              ...field.formField,
              pageIndex: field.formField.pageIndex + 1,
              pageNumber: field.formField.pageNumber + 1,
            },
          };
        } else {
          return field;
        }
      });
      updateFormFields([...updatedFields]);
      updateEnvelopeFormData('fileId');

      //TODO: send the pageDeleteIndex and store in BE.
    } catch (error) {
      console.error('Error adding page to PDF:', error);
    }
  };

  const removePageFromEnvelopePDF = async (pageIndex) => {
    try {
      const pdfDoc = await PDFDocument.load(envelopeData?.activeFile?.file);
      if (pageIndex >= 0 && pageIndex < pdfDoc.getPageCount()) {
        pdfDoc.removePage(pageIndex);
        const base64DataUri = await pdfDoc.saveAsBase64({ dataUri: true });
        updatePdfDataForPageAddRemove({
          val: envelopeData.activeFile,
          file: base64DataUri,
        });
        updateEnvelopeData('activeFile', {
          ...envelopeData.activeFile,
          file: base64DataUri,
        });
        setPagesAddedByUser((prev) => {
          const updatedIndexes = [];
          prev?.forEach((x) => {
            if (x > pageIndex) {
              updatedIndexes.push(x - 1);
            } else if (x < pageIndex) {
              updatedIndexes.push(x);
            }
          });
          return updatedIndexes;
        });
        const updatedFields = addedFormFields
          ?.filter((field) => field.formField.pageIndex !== pageIndex)
          ?.map((field) => {
            if (field.formField.pageIndex > pageIndex) {
              return {
                ...field,
                formField: {
                  ...field.formField,
                  pageIndex: field.formField.pageIndex - 1,
                  pageNumber: field.formField.pageNumber - 1,
                },
              };
            } else {
              return field;
            }
          });
        updateFormFields([...updatedFields]);
        setPageDeleteIndex(false);
        updateEnvelopeFormData('fileId');

        //TODO: send the pageDeleteIndex and store in BE.
      } else {
        console.error('Invalid page index:', pageIndex);
      }
    } catch (error) {
      console.error('Error removing page from PDF:', error);
    }
  };

  const scrollToTop = () => {
    documentRef.current?.scroll({
      top: 0,
      behavior: 'smooth',
    });
  };

  useEffect(() => {
    if (
      (isAdhoc && !addedFormFields?.length) ||
      activeFileId
    ) {
      const key = isAdhoc ? 'assetId' : 'fileId';
      updateEnvelopeFormData(key);
    }
  }, [addedFormFields, activeFileId]);

  return {
    envelopeData,
    activeFile,
    activeFileId,
    activeFileType,
    fileLinkTop,
    fileLinkBottom,
    isAdhoc,
    setIsAdhoc,
    scrollToTop,
    setActiveFileId,
    setActiveFileData,
    setFileTopAndBottom,
    setAttachedFilesInEnvelope,
    updateEnvelopeFormData,
    updateAllFileStates,
    addPageInEnvelopePDF,
    removePageFromEnvelopePDF,
  };
}
